/**
 * ERROR BOUNDARY - ChatbotImóveis®
 *
 * Componente React para capturar e tratar erros globais.
 * Evita que a aplicação inteira quebre por erros isolados.
 *
 * Features:
 * - Captura erros em componentes filhos
 * - UI amigável de erro
 * - Botão de retry
 * - Log de erros para monitoramento
 * - Cores alinhadas com /brand
 *
 * Baseado em: React Error Boundaries (React 18)
 */

import { Component, ReactNode, ErrorInfo } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';

// ============================================================================
// TIPOS
// ============================================================================

interface ErrorBoundaryProps {
  /**
   * Componentes filhos a serem renderizados
   */
  children: ReactNode;

  /**
   * Callback chamado quando erro ocorre
   */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;

  /**
   * Mensagem customizada de erro
   */
  fallbackMessage?: string;

  /**
   * Mostrar detalhes técnicos do erro (apenas em desenvolvimento)
   */
  showDetails?: boolean;
}

interface ErrorBoundaryState {
  /**
   * Indica se há erro ativo
   */
  hasError: boolean;

  /**
   * Objeto de erro capturado
   */
  error: Error | null;

  /**
   * Stack trace do erro
   */
  errorInfo: ErrorInfo | null;
}

// ============================================================================
// COMPONENTE (Class Component - React Error Boundaries requerem)
// ============================================================================

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  /**
   * Método estático chamado quando erro ocorre
   */
  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  /**
   * Método chamado após erro ser capturado
   */
  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log do erro para console (ou serviço de monitoramento)
    console.error('ErrorBoundary capturou erro:', error, errorInfo);

    // Atualizar state com informações completas do erro
    this.setState({
      errorInfo,
    });

    // Callback customizado (ex: enviar para Sentry)
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // TODO: Enviar para serviço de monitoramento (Sentry, etc.)
    // Exemplo:
    // Sentry.captureException(error, {
    //   contexts: {
    //     react: {
    //       componentStack: errorInfo.componentStack,
    //     },
    //   },
    // });
  }

  /**
   * Resetar estado de erro (retry)
   */
  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  /**
   * Redirecionar para home
   */
  handleGoHome = () => {
    window.location.href = '/';
  };

  /**
   * Render
   */
  render() {
    const { hasError, error, errorInfo } = this.state;
    const {
      children,
      fallbackMessage,
      showDetails = process.env.NODE_ENV === 'development',
    } = this.props;

    if (hasError) {
      return (
        <div className='min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4'>
          <Card className='max-w-2xl w-full border-2 border-red-200 dark:border-red-800'>
            <CardHeader className='bg-red-50 dark:bg-red-950 border-b border-red-200 dark:border-red-800'>
              <div className='flex items-center gap-3'>
                <div className='w-12 h-12 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center'>
                  <AlertTriangle className='w-6 h-6 text-red-600 dark:text-red-400' />
                </div>
                <CardTitle className='text-red-900 dark:text-red-100'>
                  Ops! Algo deu errado
                </CardTitle>
              </div>
            </CardHeader>

            <CardContent className='p-6 space-y-6'>
              {/* Mensagem principal */}
              <div className='text-gray-700 dark:text-gray-300'>
                <p className='text-lg mb-2'>
                  {fallbackMessage || 'Ocorreu um erro inesperado na aplicação.'}
                </p>
                <p className='text-sm text-gray-600 dark:text-gray-400'>
                  Você pode tentar recarregar a página ou voltar para a página inicial.
                </p>
              </div>

              {/* Detalhes técnicos (apenas em desenvolvimento) */}
              {showDetails && error && (
                <div className='bg-gray-100 dark:bg-gray-800 rounded-lg p-4 border border-gray-300 dark:border-gray-700'>
                  <h4 className='font-semibold text-sm text-gray-900 dark:text-gray-100 mb-2'>
                    Detalhes técnicos:
                  </h4>
                  <div className='space-y-2'>
                    <div>
                      <span className='text-xs font-mono text-red-600 dark:text-red-400 block'>
                        {error.name}: {error.message}
                      </span>
                    </div>
                    {errorInfo && errorInfo.componentStack && (
                      <div className='mt-2'>
                        <span className='text-xs font-mono text-gray-600 dark:text-gray-400 block whitespace-pre-wrap overflow-x-auto max-h-40'>
                          {errorInfo.componentStack}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Ações */}
              <div className='flex flex-col sm:flex-row gap-3'>
                <Button
                  onClick={this.handleReset}
                  className='flex-1 bg-gradient-to-r from-[#1E40AF] to-[#3B82F6] hover:from-[#1E3A8A] hover:to-[#2563EB]'
                >
                  <RefreshCw className='w-4 h-4 mr-2' />
                  Tentar Novamente
                </Button>

                <Button
                  onClick={this.handleGoHome}
                  variant='outline'
                  className='flex-1 border-gray-300 dark:border-gray-600'
                >
                  <Home className='w-4 h-4 mr-2' />
                  Ir para Página Inicial
                </Button>
              </div>

              {/* Info de suporte */}
              <div className='text-center pt-4 border-t border-gray-200 dark:border-gray-700'>
                <p className='text-sm text-gray-600 dark:text-gray-400'>
                  Se o problema persistir, entre em contato com o{' '}
                  <a
                    href='mailto:suporte@chatbotimoveis.com.br'
                    className='text-[#1E40AF] hover:text-[#3B82F6] font-semibold'
                  >
                    suporte
                  </a>
                  .
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return children;
  }
}
